import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

const EXPENSE_KEYS = [
    { key: 'adSpend', label: 'Ad Spend', color: CHART_COLORS.category.advertising },
    { key: 'discounts', label: 'Discounts', color: CHART_COLORS.category.discounts },
    { key: 'returns', label: 'Returns', color: CHART_COLORS.category.returns },
    { key: 'transactionFees', label: 'Payment Fees', color: CHART_COLORS.category.fees },
];

export const ExpenseCategoryTrendChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const metrics = finance.dailyBreakdown![date]?.metrics || {};
            return {
                date,
                formattedDate: format(parseISO(date), 'MMM dd'),
                adSpend: Math.abs(Number(metrics.adSpend) || 0),
                discounts: Math.abs(Number(metrics.discounts) || 0),
                returns: Math.abs(Number(metrics.returns) || 0),
                transactionFees: Math.abs(Number(metrics.transactionFees) || 0),
            };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, name: string) => {
                                const entry = EXPENSE_KEYS.find((e) => e.key === name);
                                return [
                                    formatCompactNumber(value, currencySymbol),
                                    t(`charts.expenseCategory.${entry?.label || name}`, entry?.label || name),
                                ];
                            }}
                            labelFormatter={(label: string) => label}
                        />
                        <Legend
                            formatter={(value: string) => {
                                const entry = EXPENSE_KEYS.find((e) => e.key === value);
                                return t(`charts.expenseCategory.${entry?.label || value}`, entry?.label || value);
                            }}
                        />
                        {EXPENSE_KEYS.map((expense) => (
                            <Area
                                key={expense.key}
                                type="monotone"
                                dataKey={expense.key}
                                stackId="expenses"
                                stroke={expense.color}
                                fill={expense.color}
                                fillOpacity={0.3}
                                strokeWidth={CHART_STYLES.line.strokeWidth}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
